import { useState } from "react";
import { Plus, Layers, Sparkles } from "lucide-react";
import { Button } from "@/modern/components/ui/button-shadcn";
import { cn } from "@/modern/utils";
import { CONTEXT_GUIDE_TARGETS, guideTargetProps } from "@/react/guides";
import { useContextPage } from "../context/ContextPageContext";
import { ContextCard } from "./ContextCard";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { boardContextApi } from "@/network/network.jsx"; // eslint-disable-line @typescript-eslint/no-unsafe-assignment
import { showToast } from "@/other/utils/utils.jsx"; // eslint-disable-line @typescript-eslint/no-unsafe-call
import { OPENAI_KEY_REQUIRED_TITLE, useHasOpenAIKey } from "@/react/hooks/useHasOpenAIKey";
import type { BoardContext } from "@/bryntum-lib/models/BoardContext";

const DraftCard = () => (
  <div className="flex items-center gap-2.5 rounded-lg px-3.5 py-2.5 bg-primary/[0.08] border-l-2 border-l-primary">
    <Sparkles className="h-3 w-3 text-primary shrink-0" />
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-foreground/70 italic truncate">New Context…</p>
      <p className="text-[11px] text-muted-foreground mt-0.5">Continue setup</p>
    </div>
  </div>
);

const EmptyState = ({
  onNew,
  hasOpenAIKey,
}: {
  onNew: () => void;
  hasOpenAIKey: boolean;
}) => (
  <div className="flex flex-col items-center justify-center gap-4 px-6 py-16 text-center">
    <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-border bg-muted">
      <Layers className="h-5 w-5 text-muted-foreground" />
    </div>
    <div>
      <p className="text-sm font-medium text-foreground">No contexts yet</p>
      <p className="mt-1 text-xs text-muted-foreground leading-relaxed max-w-[180px]">
        Create your first context to help Swivu understand your organization.
      </p>
    </div>
    <Button
      size="sm"
      onClick={onNew}
      disabled={!hasOpenAIKey}
      title={!hasOpenAIKey ? OPENAI_KEY_REQUIRED_TITLE : undefined}
      className="gap-1.5"
    >
      <Plus className="h-3.5 w-3.5" />
      New Context
    </Button>
  </div>
);

const triggerDownload = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

export const ContextList = () => {
  const {
    contexts,
    listSelectionId,
    selectContext,
    deleteContext,
    duplicateContext,
    beginNewContext,
    isCreatingNew,
    isDuplicating,
  } = useContextPage();
  const hasOpenAIKey = useHasOpenAIKey();
  const [exportingId, setExportingId] = useState<number | null>(null);
  const { selectedBoard } = useSwivuBryntumContext() as any;

  const handleExport = async (contextId: number, contextName: string | null, status: BoardContext["status"]) => {
    if (status !== "completed") {
      showToast({ msg: "Only completed contexts can be exported.", type: "error", duration: 4000 });
      return;
    }
    const slug = selectedBoard?.record?.slug as string | undefined;
    if (!slug) {
      showToast({ msg: "Unable to export — board not found.", type: "error", duration: 3000 });
      return;
    }
    setExportingId(contextId);
    try {
      const response = await boardContextApi(slug).exportMarkdown(contextId);
      const blob = new Blob([response.data], { type: "text/markdown;charset=utf-8" });
      const disposition: string = response.headers?.["content-disposition"] ?? "";
      const match = disposition.match(/filename[^;=\n]*=["']?([^"';\n]+)/i);
      const filename = match?.[1]?.trim()
        ?? `${slug}-${(contextName ?? "context").replace(/\s+/g, "-").toLowerCase()}.md`;
      triggerDownload(blob, filename);
    } catch {
      showToast({ msg: "Export failed. Please try again.", type: "error", duration: 3000 });
    } finally {
      setExportingId(null);
    }
  };

  return (
    <div className="flex flex-col h-full min-h-0" {...guideTargetProps(CONTEXT_GUIDE_TARGETS.contextList)}>
      <div className="shrink-0 flex items-center px-4 h-12 border-b border-border/40">
        <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground/70">
          Contexts
          {contexts.length > 0 && (
            <span className="ml-1.5 font-normal normal-case tracking-normal text-muted-foreground">{contexts.length}</span>
          )}
        </span>
      </div>

      <div className={cn("flex-1 min-h-0 overflow-y-auto")}>
        {contexts.length === 0 && !isCreatingNew ? (
          <EmptyState onNew={beginNewContext} hasOpenAIKey={hasOpenAIKey} />
        ) : (
          <div className="flex flex-col gap-1 p-2">
            {isCreatingNew && <DraftCard />}
            {contexts.map((ctx) => (
              <ContextCard
                key={ctx.id}
                context={ctx}
                contexts={contexts}
                isSelected={ctx.id === listSelectionId}
                isExporting={exportingId === ctx.id}
                hasOpenAIKey={hasOpenAIKey}
                onSelect={() => selectContext(ctx.id)}
                onDelete={() => deleteContext(ctx.id)}
                onExport={() => handleExport(ctx.id, ctx.name, ctx.status)}
                onDuplicate={() => duplicateContext(ctx.id)}
                isDuplicating={isDuplicating}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
